import axios from "axios";
import { toast } from "react-toastify";
import { generateSesid } from "utilities/generateSesid";
import {
    buildXborderFields,
    buildUsdPayloadFromCrossBorder,
    validateXborder,
} from "./helpers";

const buildSource = (provider) => {
    const name = provider?.name || provider?.bankName || "";
    return name || undefined;
};

const buildSavedRecipientReference = (...sources) => {
    for (const source of sources) {
        if (source?.beneficiaryId) return { beneficiaryId: source.beneficiaryId };
        if (source?.transactionId) return { transactionId: source.transactionId };
    }

    return {};
};

export async function submitReviewTransfer({
    pin,
    businessId,
    isCryptoFlow,
    isCryptoToFiatFlow,
    isCrossBorder,
    isBankRecipient,
    currency,
    cryptoTransfer,
    cfAmount,
    cfCoin,
    cfNetwork,
    cfRecipient,
    cfMemo,
    cfPayoutCurrency,
    cb,
    cbTo,
    cbFrom,
    cbFields,
    cbBankName,
    cbMemo,
    cbInvoice,
    cbOtherDoc,
    cbUsd,
    cbAmount,
    cbSource,
    usd,
    usdPayloadFromStore,
    recipientMemo,
    recipientAmount,
    recipientInvoice,
    recipientOtherDocument,
    transferData,
    bankRecipient,
    bankProvider,
}) {
    const headers = { headers: { "x-business-id": businessId } };
    const sesid = generateSesid(businessId);
    const savedRecipientReference = buildSavedRecipientReference(
        bankRecipient,
        usdPayloadFromStore,
        transferData?.recipient
    );

    let endpoint = "";
    let payload = {};

    if (isCryptoFlow) {
        endpoint = "/v1/wallets/transfer-crypto";
        payload = {
            address: cryptoTransfer?.address || "",
            amount: String(cryptoTransfer?.amount ?? ""),
            naration: cryptoTransfer?.naration || "",
            currency: cryptoTransfer?.token || "",
            network: cryptoTransfer?.network,
            pin,
            ...(cryptoTransfer?.invoice && {
                invoice: {
                    url: cryptoTransfer.invoice.url || "",
                    name: cryptoTransfer.invoice.name || "",
                },
            }),
            ...(cryptoTransfer?.supportDocument && {
                supportDocument: {
                    url: cryptoTransfer.supportDocument.url || "",
                    name: cryptoTransfer.supportDocument.name || "",
                },
            }),
        };
    } else if (isCryptoToFiatFlow) {
        endpoint = "/v1/wallets/crypto/bank-withdraw";
        payload = {
            amount: String(cfAmount || ""),
            recipient: {
                institution: cfRecipient?.institution || "",
                accountIdentifier: cfRecipient?.accountIdentifier || "",
                accountName: cfRecipient?.accountName || "",
                bankName: cfRecipient?.bankName || "",
                memo: cfMemo || "",
                currency: cfPayoutCurrency || "",
            },
            pin,
            currency: cfCoin,
            network: cfNetwork || "",
        };
    } else if (isCrossBorder) {
        endpoint = "/v1/wallets/cross-currency/transfer";
        const crossBorderSavedReference = buildSavedRecipientReference(cb?.recipient);

        if (String(cbTo).toUpperCase() === "USD") {
            const usdPayload = buildUsdPayloadFromCrossBorder(cb, pin, sesid);
            const acctType = String(cb?.recipient?.usd?.account_type || "us").toLowerCase();
            const missing = [];

            if (!usdPayload.bank_name) missing.push("bank_name");
            if (!usdPayload.account_owner_name) missing.push("account_owner_name");
            if (!usdPayload.address?.street_line_1) missing.push("address.street_line_1");
            if (!usdPayload.address?.city) missing.push("address.city");
            if (!usdPayload.address?.state) missing.push("address.state");
            if (!usdPayload.address?.postal_code) missing.push("address.postal_code");
            if (!usdPayload.address?.country) missing.push("address.country");

            if (acctType === "us") {
                if (!usdPayload.account?.routing_number) missing.push("account.routing_number");
                if (!usdPayload.account?.account_number) missing.push("account.account_number");
            }
            if (acctType === "iban") {
                if (!usdPayload.iban?.account_number) missing.push("iban.account_number");
                if (!usdPayload.iban?.bic) missing.push("iban.bic");
                if (!usdPayload.iban?.country) missing.push("iban.country");
            }
            if (acctType === "international_wire" || acctType === "swift") {
                if (!usdPayload.swift?.account_number) missing.push("swift.account_number");
                if (!usdPayload.swift?.bic) missing.push("swift.bic");
                if (!usdPayload.swift?.country) missing.push("swift.country");
            }

            if (
                !crossBorderSavedReference.beneficiaryId &&
                !crossBorderSavedReference.transactionId &&
                missing.length
            ) {
                toast.error(`Missing required fields for USD: ${missing.join(", ")}`);
                return undefined;
            }

            payload = {
                ...usdPayload,
                ...crossBorderSavedReference,
                ...(cbInvoice?.url && { invoice: cbInvoice.url }),
                ...(cbOtherDoc?.url && { supportDocument: cbOtherDoc.url }),
                ...(cbSource && { source: cbSource }),
            };
        } else {
            const fields = buildXborderFields(cbTo, cbFields, cbBankName, cbUsd);
            const { ok, missing } = validateXborder(cbTo, fields, cbUsd?.account_type || "us");
            if (
                !crossBorderSavedReference.beneficiaryId &&
                !crossBorderSavedReference.transactionId &&
                !ok
            ) {
                toast.error(`Missing required fields for ${cbTo}: ${missing.join(", ")}`);
                return undefined;
            }

            payload = {
                amount: String(cbAmount || ""),
                sesid,
                pin,
                naration: cbMemo || "",
                currencyFrom: (cbFrom || "").toUpperCase(),
                isConversion: false,
                currencyTo: (cbTo || "").toUpperCase(),
                ...(crossBorderSavedReference.beneficiaryId || crossBorderSavedReference.transactionId
                    ? {}
                    : fields),
                ...crossBorderSavedReference,
                ...(cb?.rateKey ? { rateKey: cb.rateKey } : {}),
                ...(cbInvoice?.url && { invoice: cbInvoice.url }),
                ...(cbOtherDoc?.url && { supportDocument: cbOtherDoc.url }),
                ...(cbSource && { source: cbSource }),
            };
        }
    } else if (isBankRecipient && String(currency).toUpperCase() === "USD") {
        endpoint = "/v1/wallets/fx/transfer";
        payload = {
            ...usdPayloadFromStore,
            ...savedRecipientReference,
            pin,
            sesid,
            naration: (usd?.narration || recipientMemo || transferData?.memo || "").trim(),
            amount: String(usd?.amount || recipientAmount || transferData?.amount || ""),
            currency: "USD",
            currencyFrom: "USD",
        };

        if (recipientInvoice?.url) payload.invoice = recipientInvoice.url;
        if (recipientOtherDocument?.url) payload.supportDocument = recipientOtherDocument.url;

        const usdSource = buildSource(bankProvider);
        if (usdSource) payload.source = usdSource;
    } else if (isBankRecipient) {
        endpoint = "/v1/wallets/transfer";
        const source = buildSource(bankProvider);
        payload = {
            amount: recipientAmount || "N/A",
            naration: (recipientMemo || "").trim() || "N/A",
            invoice: recipientInvoice?.url || "N/A",
            supportDocument: recipientOtherDocument?.url || "N/A",
            currency,
            sesid,
            pin,
            ...savedRecipientReference,
        };

        if (source) payload.source = source;

        if (!savedRecipientReference.beneficiaryId && !savedRecipientReference.transactionId) {
            payload = {
                ...payload,
                accountname: bankRecipient?.name || "N/A",
                accountnumber: bankRecipient?.accountNumber || "N/A",
                accountcode: bankRecipient?.bankCode || "N/A",
                bankname: bankRecipient?.bankName || "N/A",
            };
        }
    } else {
        endpoint = "/v1/wallets/transfer_to_wallet";
        const source = buildSource(transferData?.provider);
        payload = {
            amount: transferData?.amount || "N/A",
            naration: (transferData?.memo || "").trim() || "N/A",
            invoice: transferData?.invoice?.url || "N/A",
            supportDocument: transferData?.otherDocument?.url || "N/A",
            accountnumber: transferData?.recipient?.accountNumber || "N/A",
            currency: transferData?.currency || "N/A",
            pin,
            ...(source && { source }),
        };
    }

    const { data } = await axios.post(endpoint, payload, headers);
    return data?.data?.transactionId || null;
}
